import React, { useRef, useState } from 'react'
import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { CheckIcon } from '@heroicons/react/24/solid'

import { Card, CardContent, CardHeader, CardTitle } from 'shadcdn/ui/card'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from 'shadcdn/ui/form'
import { ArrayTextAreaFormField, TextAreaFormField, TextInputFormField } from 'shadcdn/custom/Form'
import type { CategoryWithChildren } from 'features/categories/interfaces'
import { Label } from 'shadcdn/ui/label'
import { Input } from 'shadcdn/ui/input'
import { useToast } from 'shadcdn/ui/use-toast'
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from 'shadcdn/ui/select'
import { Button } from 'ui/Button/Button'
import { SoftwareAdminApi, type SoftwareSchema, SoftwareStatus } from 'openapi'
import { uploadImageCover } from 'features/admin/utlls'
import { isYoutubeUrl } from 'features/software/utils'

import { CategorySelectField } from './CategorySelectField'
import { WarningBgIfEmpty } from './WarningBgIfEmpty'

type Props = {
  software: SoftwareSchema
  categories: CategoryWithChildren[]
  onPostSave: () => void
}

const youtubeUrlSchema = z.union([
  z.string().url().refine(isYoutubeUrl, { message: 'Invalid YouTube URL' }),
  z.string().startsWith('#'),
  z.string().max(0),
])

const documentationUrlSchema = z.union([z.string().url(), z.string().max(0), z.string().startsWith('#')])

const adminFormSchema = z.object({
  name: z.string().min(1, 'Required Field'),
  vendor: z.string().min(1, 'Required Field'),
  description: z.string().optional(),
  internalNotes: z.string().optional(),
  url: z.union([z.literal(''), z.string().trim().url()]),
  category_id: z.number().optional().nullable(),

  youtubeReviewsUrls: z.array(youtubeUrlSchema),
  youtubeTutorialsUrls: z.array(youtubeUrlSchema),
  youtubeOtherUrls: z.array(youtubeUrlSchema),

  documentationUrls: z.array(documentationUrlSchema),

  status: z
    .literal(SoftwareStatus.BLANK)
    .or(z.literal(SoftwareStatus.ENABLED))
    .or(z.literal(SoftwareStatus.DISABLED))
    .or(z.literal(SoftwareStatus.REJECTED))
    .or(z.literal(SoftwareStatus.PENDING))
    .or(z.literal(SoftwareStatus.ERROR))
    .or(z.literal(SoftwareStatus.VENDOR_PAYWALL))
    .optional(),
})

type AdminFormSchema = z.infer<typeof adminFormSchema>

export const AdminSoftwareForm = React.memo((props: Props) => {
  const { software, categories, onPostSave } = props

  const form = useForm<AdminFormSchema>({
    resolver: zodResolver(adminFormSchema),
    defaultValues: {
      ...software,
    },
  })
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const { toast } = useToast()

  async function onSubmit(values: AdminFormSchema) {
    setIsSubmitting(true)
    if (imageBlob.current) {
      await uploadImageCover(imageBlob.current, software.id)
      imageBlob.current = null
    }
    await SoftwareAdminApi.update({ id: software.id, requestBody: values })
    toast({
      // prettier-ignore
      title: (
        <div>
          <CheckIcon className="w-4 h-4 -t-1 inline" /> Saved
        </div>
      ) as any, // shitty cast
      className: 'bg-white !l-0 !t-0',
    })
    onPostSave()
    setIsSubmitting(false)
  }

  const imageBlob = useRef<Blob | null>(null)

  function handlePasteImage(blob: Blob) {
    imageBlob.current = blob
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8 flex-1 ">
        <Card className="mx-auto">
          <CardHeader className="pb-0">
            <div className="flex">
              <CardTitle className="flex-grow">
                {software.name} <span className="text-gray-500 font-light">by {software.vendor} </span>
                {/*<GoogleSearchLink search={`${software.name} ${software.vendor}`}>🔍 search</GoogleSearchLink>*/}
              </CardTitle>

              <div>
                <FormField
                  control={form.control}
                  name={'status'}
                  render={({ field }) => (
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          {Object.values(SoftwareStatus).map((status) => (
                            <SelectItem key={status} value={status} className="capitalize">
                              {status.replace('_', ' ')}
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>
            </div>
          </CardHeader>

          <CardContent className="flex flex-col gap-y-4 ">
            <div className="flex flex-row gap-4">
              <div className="flex-grow flex flex-col gap-4">
                <div className="flex flex-row gap-4">
                  <TextInputFormField className="basis-1/4" form={form} name="name" label="Name" />
                  <TextInputFormField className="basis-1/4" form={form} name="vendor" label="Vendor" />

                  <FormField
                    control={form.control}
                    name={'category_id'}
                    render={({ field }) => (
                      <FormItem className="basis-1/4">
                        <FormLabel>Category</FormLabel>
                        <FormControl>
                          <CategorySelectField field={field} categories={categories} className="w-full py-4" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <WarningBgIfEmpty checkValue={form.watch('url')} className="basis-1/4">
                    <TextInputFormField form={form} name="url" label="Official Website" />
                  </WarningBgIfEmpty>
                </div>

                <WarningBgIfEmpty checkValue={form.watch('description')}>
                  <TextAreaFormField
                    form={form}
                    name="description"
                    label="Description"
                    className="[&_textarea]:text-xs [&_textarea]:font-mono"
                    growWrapClassName="text-xs font-mono"
                  />
                </WarningBgIfEmpty>
              </div>

              <div className="flex flex-col gap-4">
                <WarningBgIfEmpty checkValue={software.cover}>
                  <Label>Cover</Label>
                  <ImagePasteInput onPasteImage={handlePasteImage} defaultImage={software.cover} />
                </WarningBgIfEmpty>
              </div>
            </div>

            <hr className="border-t border-gray-200 my-4" />

            <h2 className="text-xl">Youtube and manuals</h2>

            <div className="grid grid-cols-2 gap-4">
              <ArrayTextAreaFormField
                form={form}
                name="youtubeReviewsUrls"
                label="Youtube Reviews URLs"
                className="[&_textarea]:text-[0.7rem] [&_textarea]:font-mono w-full"
                growWrapClassName="text-xs font-mono"
                disabled={isSubmitting}
              />
              <ArrayTextAreaFormField
                form={form}
                name="youtubeTutorialsUrls"
                label="Youtube Tutorials URLs"
                className="[&_textarea]:text-[0.7rem] [&_textarea]:font-mono w-full"
                growWrapClassName="text-xs font-mono"
                disabled={isSubmitting}
              />
              <ArrayTextAreaFormField
                form={form}
                name="youtubeOtherUrls"
                label="Youtube Other URLs"
                className="[&_textarea]:text-[0.7rem] [&_textarea]:font-mono w-full"
                growWrapClassName="text-xs font-mono"
                disabled={isSubmitting}
              />
              <ArrayTextAreaFormField
                form={form}
                name="documentationUrls"
                label="Manual (documentation) URLs"
                className="[&_textarea]:text-[0.7rem] [&_textarea]:font-mono w-full"
                growWrapClassName="text-xs font-mono"
                disabled={isSubmitting}
              />
            </div>

            <hr className="border-t border-gray-200" />

            <TextAreaFormField
              form={form}
              name="internalNotes"
              label="Notes"
              helpText={
                <>
                  Internal notes for the team; hidden from public but can be easily extracted, so don't put sensitive
                  information here.
                </>
              }
              className="[&_textarea]:text-xs [&_textarea]:font-mono [&_textarea]:bg-yellow-50"
              growWrapClassName="text-xs font-mono"
            />
            <div className="flex justify-center gap-3 w-full">
              <Button type="submit" disabled={isSubmitting} spinner={isSubmitting}>
                Save Software
              </Button>
            </div>
          </CardContent>
        </Card>
      </form>
    </Form>
  )
})

type ImagePasteInputProps = {
  onPasteImage: (blob: any) => void
  defaultImage?: string | null
}
// input that listens paste event and show image pasted in it
const ImagePasteInput = (props: ImagePasteInputProps) => {
  const { onPasteImage, defaultImage, ...restProps } = props
  const [image, setImage] = React.useState<string | null>(defaultImage ?? null)

  function handlePaste(e: React.ClipboardEvent<HTMLInputElement>) {
    const items = e.clipboardData?.items
    const imageItem = Array.from(items || []).find((item) => item.type.indexOf('image') !== -1)
    const blob = imageItem?.getAsFile()
    if (!blob) return

    onPasteImage(blob)
    const reader = new FileReader()
    reader.onload = (e) => setImage(e.target?.result as string)
    reader.readAsDataURL(blob)
  }

  return (
    <div className="w-full">
      <CoverImage src={image} />
      <Input {...restProps} onPaste={handlePaste} placeholder="Paste image here..." className="mt-2 w-48" />
    </div>
  )
}

function CoverImage(props: { src?: string | null }) {
  const { src } = props
  return (
    <>
      {src ? (
        <a href={src} target="_blank">
          <img src={src} className="max-w-56" />
        </a>
      ) : (
        <div className="bg-muted h-48 w-48 rounded-xl flex items-center justify-center">
          <div className="text-center text-gray-400">No image</div>
        </div>
      )}
    </>
  )
}

// const GoogleSearchLink = (props: { search: string; children: React.ReactNode }) => {
//   return (
//     <a href={`https://google.com/search?q=${props.search}`} target="_blank">
//       {props.children}
//     </a>
//   )
// }
